import {Matcher} from '@/lib/axiosAddTransforms'
import {
  offsetResponse,
  orderRequest,
  payResponse,
  transactionDateResponse,
  payDateResponse,
  dashDateResponse,
} from '@/transformers/utils'

const matchers: Matcher[] = [
  {
    test: /^\/invoices\/?$/,
    method: 'get',
    transform: {
      request: (payload) => {
        const {order} = payload.params
        const {bizId} = payload.data
        payload.params.o = orderRequest(order)
        payload.params.status = payload.params.status.join(',')
        payload.url = `/bizs/${bizId}/transactions/`
        delete payload.params.order
        delete payload.data.bizId
        return payload
      },
      response: (data) => {
        if(!data || !data.results) {
          return {}
        }
        return {
          results: data.results.map((result) => invoiceListResponse(result)),
          ...propertyResponse(data),
        }
      },
    },
  },
  {
    test: /^\/invoices\/\$id\/?$/,
    method: 'get',
    transform: {
      request: (payload) => {
        const {bizId, id} = payload.data
        payload.url = `/bizs/${bizId}/transactions/${id}`
        return payload
      },
      response: (data) => {
        if(!data || !data.id) {
          return data
        }

        return invoiceResponse(data)
      },
    },
  },
  {
    test: /^\/invoices\/\$id\/?$/,
    method: 'patch',
    transform: {
      request: (payload) => {
        const {bizId, id, bizSummary, clientSummary, clientBankAccount} = payload.data
        payload.data = {
          bizBankBookSummary: bizSummary,
          clientBankBookSummary: clientSummary,
          clientBankAccount,
        }
        payload.url = `/bizs/${bizId}/transactions/${id}`
        return payload
      },
      response: (data) => {
        if(!data || !data.id) {
          return data
        }

        return invoiceResponse(data)
      },
    },
  },
  {
    test: /^\/invoices\/\$id\/status?$/,
    method: 'post',
    transform: {
      request: (payload) => {
        const {id, bizId} = payload.data
        payload.url = `/bizs/${bizId}/transactions/${id}/status`
        delete payload.data.id
        delete payload.data.bizId
        return payload
      },
      response: (data) => {
        if(!data || !data.id) {
          return data
        }
        return invoiceResponse(data)
      },
    },
  },
  {
    test: /^\/invoices\/summary/,
    method: 'get',
    transform: {
      request: (payload) => {
        const {bizId} = payload.params
        payload.url = `/bizs/${bizId}/transactions/summary`
        delete payload.params.bizId
        return payload
      },
      response: (data) => {
        if(!data) {
          return data
        }
        return data
      },
    },
  },
]

const invoiceListResponse = (data) => {
  const {id, sharedData = {}, errorCode, client = {representative: {}}, clientBankAccount = {},
    paidAt, clientBankBookSummary, bizBankBookSummary, isForcedPayment
  } = data
  return {
    id: String(id),
    status: statusResponse(data),
    amount: payResponse(sharedData.amountTotal),
    tradedAt: dashDateResponse(sharedData.tradedAt),
    mainItemName: sharedData.mainItemName,
    errorMessage: errorCode,
    clientName: client.name,
    clientRepresentativeName: client.representative?.name,
    clientBizRegistrationNumber: client.bizRegistrationNumber,
    clientId: String(client.id),
    isForcedPayment,
    hasClientBankAccount: Boolean(clientBankAccount.bankCode && clientBankAccount.number),
    clientBankAccount: clientBankAccount.bankCode && clientBankAccount.number
      ? {
          id: String(clientBankAccount.id),
          bank: clientBankAccount.bankCode,
          number: clientBankAccount.number,
          owner: clientBankAccount.ownerName,
        }
      : null,
    clientSummary: clientBankBookSummary,
    bizSummary: bizBankBookSummary,
    paidAt: transactionDateResponse(paidAt),
  }
}

const invoiceResponse = (data) => {
  const {id, sharedData = {}, errorCode, client = {},
    clientBankAccount = {}, paidAt, virtualEvidences, clientBankBookSummary,
    bizBankBookSummary, isForcedPayment, transferFee, bizBankAccount
  } = data
  const {rawData} = virtualEvidences[0]
  return {
    id: String(id),
    status: statusResponse(data),
    amount: payResponse(sharedData.amountTotal),
    tradedAt: dashDateResponse(sharedData.tradedAt),
    errorMessage: errorCode,
    clientId: String(client.id),
    isForcedPayment,
    hasClientBankAccount: Boolean(clientBankAccount.bankCode && clientBankAccount.number),
    clientBankAccount: {
      id: String(clientBankAccount.id),
      bank: clientBankAccount.bankCode,
      number: clientBankAccount.number,
      owner: clientBankAccount.ownerName,
    },
    bizBankAccount,
    clientSummary: clientBankBookSummary,
    bizSummary: bizBankBookSummary,
    paidAt: transactionDateResponse(paidAt),
    transferFee: payResponse(transferFee),
    clientName: rawData.supCorpNm,
    supRepNm: rawData.supRepNm,
    supAddress: rawData.supAddress,
    supBizNo: rawData.supBizNo,
    supEmail: rawData.supEmail,
    itemNm: rawData.itemNm,
    issueNo: rawData.issueNo,
    makeDt: payDateResponse(rawData.makeDt),
    issueDt: payDateResponse(rawData.issueDt),
    sendDt: payDateResponse(rawData.sendDt),
    taxClsf: rawData.taxClsf,
    supAmt: payResponse(rawData.supAmt),
    totAmt: payResponse(rawData.totAmt),
    taxAmt: payResponse(rawData.taxAmt),
    isTaxFree: rawData.taxClsf === '계산서' || rawData.taxClsf === '수정계산서',
  }
}

const propertyResponse = (data) => {
  const {next, count} = data
  return {
    nextOffset: offsetResponse(next),
    count,
  }
}

const statusResponse = (data) => {
  const {status, isExcluded, sharedData = {}} = data
  const amount = sharedData?.amountTotal || 0
  // make excluded status
  if(status === 'completed' && isExcluded) {
    return 'excluded'
  }
  // make unavailable status
  if(status === 'pending' && Number(amount) < 0) {
    return 'unavailable'
  }
  return status
}

export {statusResponse, invoiceResponse}

export default matchers
